/**
 * Analysis component types and zone utilities
 *
 * Score zone mapping (6 tiers):
 * - 90-100: Excellent (success)
 * - 80-89: Very good (success)
 * - 70-79: Good (teal) ← CRITICAL: teal, not orange
 * - 60-69: Fair (orange)
 * - 50-59: Below average (warning)
 * - 0-49: Poor (error)
 */

export type ScoreZone = 'excellent' | 'verygood' | 'good' | 'fair' | 'belavg' | 'poor';

export type ConfidenceLevel = 'low' | 'moderate' | 'high';

/**
 * Maps a score (0-100) to its zone classification
 */
export function getScoreZone(score: number): ScoreZone {
  if (score >= 90) {return 'excellent';}
  if (score >= 80) {return 'verygood';}
  if (score >= 70) {return 'good';}
  if (score >= 60) {return 'fair';}
  if (score >= 50) {return 'belavg';}
  return 'poor';
}

/**
 * Tailwind text color class for each zone
 */
export const zoneTextColor: Record<ScoreZone, string> = {
  excellent: 'text-success',
  verygood: 'text-success',
  good: 'text-teal',
  fair: 'text-orange',
  belavg: 'text-warning',
  poor: 'text-error',
};

/**
 * Badge classes (background + text) for each zone
 */
export const zoneBadgeClasses: Record<ScoreZone, string> = {
  excellent: 'bg-success-light text-success',
  verygood: 'bg-success-light text-success',
  good: 'bg-teal-light text-teal',
  fair: 'bg-orange-light text-orange',
  belavg: 'bg-warning-light text-warning',
  poor: 'bg-error-light text-error',
};

/**
 * SVG stroke color (for inline styles) — uses rgb(var(--xxx)) pattern
 */
export const zoneStrokeVar: Record<ScoreZone, string> = {
  excellent: 'rgb(var(--success))',
  verygood: 'rgb(var(--success))',
  good: 'rgb(var(--teal))',
  fair: 'rgb(var(--orange))',
  belavg: 'rgb(var(--warning))',
  poor: 'rgb(var(--error))',
};

/**
 * Icon background Tailwind class for each zone
 */
export const zoneIconBg: Record<ScoreZone, string> = {
  excellent: 'bg-success-light',
  verygood: 'bg-success-light',
  good: 'bg-teal-light',
  fair: 'bg-orange-light',
  belavg: 'bg-warning-light',
  poor: 'bg-error-light',
};

/**
 * Zone label (human-readable)
 */
export const zoneLabel: Record<ScoreZone, string> = {
  excellent: 'Excellent',
  verygood: 'Very good',
  good: 'Good',
  fair: 'Fair',
  belavg: 'Below average',
  poor: 'Poor',
};
